import { useState } from "react";
import { LucideDownload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/modals/Modal";
import { bulkExport } from "@/utils/bulkExportService";

/**
 * Modal for bulk-exporting student reports as PDFs and/or CSV.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array} props.reports - All reports available for export
 * @param {object} props.classData - Class metadata (name, grade, schoolYear)
 */
export function ExportModal({ open, onClose, reports, classData }) {
  const [selected, setSelected] = useState(() => new Set(reports.map((r) => r._id)));
  const [exportPdfs, setExportPdfs] = useState(true);
  const [exportCsv, setExportCsv] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(false);

  // Re-sync selected when reports change (modal reopen)
  // We handle this by always using the passed reports as source of truth

  function toggleReport(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function handleExport() {
    const selectedReports = reports.filter((r) => selected.has(r._id));
    if (selectedReports.length === 0 || (!exportPdfs && !exportCsv)) return;
    setExporting(true);
    setDone(false);
    setProgress({
      done: 0,
      total: (exportPdfs ? selectedReports.length : 0) + (exportCsv ? 1 : 0),
    });
    try {
      const ok = await bulkExport({
        reports: selectedReports,
        exportPdfs,
        exportCsv,
        className:
          [classData?.grade && `Grade ${classData.grade}`, classData?.schoolYear]
            .filter(Boolean)
            .join(" - ") || classData?.name,
        onProgress: (d, t) => setProgress({ done: d, total: t }),
      });
      if (ok) setDone(true);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  }

  function handleClose() {
    if (!exporting) onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Export Reports"
      subtitle={`${reports.length} student${reports.length !== 1 ? "s" : ""} in this class`}
      icon={<LucideDownload size={16} className="text-primary" />}
      preventClose={exporting}
    >
      <div className="px-6 py-5 space-y-5 overflow-y-auto">
        {!done ? (
          <>
            {/* Student selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Select students</p>
                <div className="flex gap-2">
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                    onClick={() => setSelected(new Set(reports.map((r) => r._id)))}
                  >
                    All
                  </button>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                    onClick={() => setSelected(new Set())}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg border border-border p-2">
                {reports.map((r) => (
                  <label
                    key={r._id}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(r._id)}
                      onChange={() => toggleReport(r._id)}
                      disabled={exporting}
                      className="accent-primary"
                    />
                    <span className="text-sm text-foreground">{r.studentName}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Export type */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Export as</p>
              <div className="flex gap-2">
                <ExportTypeButton
                  label="PDF"
                  active={exportPdfs}
                  onClick={() => setExportPdfs((v) => !v)}
                  disabled={exporting}
                />
                <ExportTypeButton
                  label="CSV"
                  active={exportCsv}
                  onClick={() => setExportCsv((v) => !v)}
                  disabled={exporting}
                />
              </div>
              {!("showDirectoryPicker" in window) && (
                <p className="text-xs text-muted-foreground">
                  Files will be downloaded as a .zip (folder saving is not supported in
                  this browser).
                </p>
              )}
            </div>

            {/* Progress */}
            {exporting && progress && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Generating...</span>
                  <span>
                    {progress.done} / {progress.total}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-200"
                    style={{
                      width: `${(progress.done / progress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleExport}
              disabled={
                exporting || selected.size === 0 || (!exportPdfs && !exportCsv)
              }
            >
              {exporting
                ? "Exporting..."
                : `Export ${selected.size} report${selected.size !== 1 ? "s" : ""}`}
            </Button>
          </>
        ) : (
          <div className="text-center py-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Export complete</p>
            <p className="text-xs text-muted-foreground">
              {selected.size} report{selected.size !== 1 ? "s" : ""} saved successfully.
            </p>
            <Button variant="outline" className="mt-2" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ExportTypeButton({ label, active, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg border text-sm font-medium transition-colors",
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-border text-muted-foreground hover:border-muted-foreground/40",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
